/* External */

/* Utils */
import {
  APP_VERSION,
  BACKEND_SERVICE_HOST,
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_SAMPLE_RATE,
  DATADOG_REPLAY_SAMPLE_RATE,
  DATADOG_SERVICE,
  ENVIRONMENT,
  AUTH_SERVICE_HOST,
} from './env';
import platform from './platform';
import { addAccessTokenDataSubscriber, getUserAccount } from './accessTokenData';

/* The RUM SDK is imported lazily inside initDatadog() so it becomes a split
   chunk fetched only when telemetry is enabled; it never blocks boot. */
let datadogRum;

async function initDatadog() {
  if (import.meta.env.VITE_IS_DATADOG_ACTIVE !== 'true') {
    return;
  }
  ({ datadogRum } = await import('@datadog/browser-rum'));
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: DATADOG_SERVICE,
    sessionSampleRate: DATADOG_SAMPLE_RATE ?? 100,
    sessionReplaySampleRate: DATADOG_REPLAY_SAMPLE_RATE ?? 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    env: ENVIRONMENT || 'local',
    version: APP_VERSION,
    useCrossSiteSessionCookie: platform.datadog.useCrossSiteSessionCookie,
    allowFallbackToLocalStorage: true,
    allowedTracingUrls: [
      BACKEND_SERVICE_HOST,
      AUTH_SERVICE_HOST,
      import.meta.env.VITE_TOOLBOX_HOST,
    ],
    beforeSend,
  });
  setDatadogUser();
  listenForUserChanges();
}

export { initDatadog };

/**
 * @param {import('@datadog/browser-rum').RumEvent} event
 * @param {import('@datadog/browser-rum').RumEventDomainContext} context
 * @returns {boolean}
 */
function beforeSend(event, context) {
  const additionalProps = {};
  if (event.type === 'resource') {
    if (event.resource.type === 'fetch') {
      additionalProps.requestHeaders = context.requestInit?.headers;
      additionalProps.requestBody = context.requestInit?.body;
      additionalProps.responseHeaders = context.response?.headers;
    }

    if (event.resource.type === 'xhr') {
      additionalProps.responseHeaders = rawHttpHeadersToObject(
        context.xhr?.getAllResponseHeaders(),
      );
    }
  }
  // eslint-disable-next-line no-param-reassign -- we need to mutate the datadog event object
  event.context = {
    ...event.context,
    ...additionalProps,
  };
  return true;
}

function rawHttpHeadersToObject(headers) {
  return Object.fromEntries(
    headers?.trim().split(/[\n\r]+/)
      .map(
        (header) => header.split(':').map((keyOrValue) => keyOrValue.trim()),
      )
      .filter(([key, value]) => !!key && !!value),
  );
}

async function setDatadogUser() {
  const user = await getUserAccount();
  if (user) {
    const {
      uid,
      username,
      accountType: loginType,
      firstName,
      lastName,
    } = user;

    datadogRum.setUser({
      id: uid,
      name: `${firstName || ''} ${lastName || ''}`,
      email: username,
      loginType,
    });
  } else {
    datadogRum.clearUser();
  }
}

function listenForUserChanges() {
  addAccessTokenDataSubscriber(setDatadogUser);
}
